import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, TextField, Button,
    ToggleButtonGroup, ToggleButton, Alert, CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import whatsappServices from "./whatsappServices";
import { SIN_ZOOM_IPHONE } from "./sinZoomIphone";

// El nombre del contacto solo sirve de saludo si es un nombre: muchos contactos quedan
// guardados con el número.
const nombreDeSaludo = (contact) => {
    const nombre = (contact?.name || "").trim();
    return nombre && !/^[\d+\s-]+$/.test(nombre) ? nombre.split(/\s+/)[0] : "";
};

/**
 * Mensaje con plantilla aprobada por Meta: la única forma de escribirle a alguien que no nos
 * escribió en las últimas 24 horas. Sin `conversation` es para un número nuevo y pide el
 * teléfono; con `conversation` retoma esa conversación.
 */
export default function TemplateMessageDialog({ open, conversation, onClose, onSent }) {
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const [values, setValues] = useState({});
    const [phone, setPhone] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    const isNew = !conversation;

    useEffect(() => {
        if (!open) return;
        setError(null);
        setPhone("");
        setLoadingTemplates(true);
        whatsappServices.getTemplates()
            .then(list => {
                setTemplates(list);
                const inicial = list.find(t => t.name === (isNew ? "haulers_comercial" : "haulers_soporte")) || list[0];
                setTemplateName(inicial?.name || "");
                // Valores por plantilla, para no perder lo escrito al cambiar de una a otra.
                const saludo = nombreDeSaludo(conversation?.contact);
                setValues(Object.fromEntries(list.map(t => [
                    t.name,
                    t.params.map((p, i) => (i === 0 ? saludo : p.default || "")),
                ])));
            })
            .catch(() => setError("No se pudieron cargar las plantillas"))
            .finally(() => setLoadingTemplates(false));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const template = templates.find(t => t.name === templateName);
    const current = values[templateName] || [];

    const setValue = (i, v) => {
        setValues(prev => {
            const next = [...(prev[templateName] || [])];
            next[i] = v;
            return { ...prev, [templateName]: next };
        });
    };

    const preview = template
        ? template.body.replace(/\{\{(\d+)\}\}/g, (_, n) => {
            const v = (current[n - 1] || "").trim();
            return v || `[${template.params[n - 1]?.label || n}]`;
        })
        : "";

    const completo = template && template.params.every((_, i) => (current[i] || "").trim())
        && (!isNew || phone.replace(/\D/g, "").length >= 9);

    const handleSend = async () => {
        if (!completo || sending) return;
        setSending(true);
        setError(null);
        const body = { template: templateName, params: current.map(v => v.trim()) };
        try {
            const conv = isNew
                ? await whatsappServices.createConversation({ ...body, phone, name: current[0]?.trim() })
                : await whatsappServices.sendTemplate(conversation.id, body);
            onSent?.(conv);
        } catch (e) {
            setError(e?.response?.data?.error || "No se pudo enviar el mensaje");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: SIN_ZOOM_IPHONE }}>
            <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 0.5 }}>
                {isNew ? "Nuevo mensaje de WhatsApp" : "Enviar plantilla"}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: "#666", fontSize: 12, mb: 2 }}>
                    WhatsApp solo deja escribirle a alguien que no nos escribió en las últimas 24 horas con un
                    mensaje aprobado. Cuando responda, la conversación sigue normal desde el inbox.
                </Typography>

                {loadingTemplates && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {!loadingTemplates && template && (
                    <>
                        {isNew && (
                            <TextField
                                fullWidth
                                size="small"
                                label="Teléfono"
                                placeholder="+56 9 1234 5678"
                                helperText="Con código de país. Un celular chileno de 9 dígitos se completa solo con el +56."
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                                autoFocus
                                sx={{ mb: 2 }}
                            />
                        )}

                        <ToggleButtonGroup
                            value={templateName}
                            exclusive
                            onChange={(_, v) => v && setTemplateName(v)}
                            size="small"
                            sx={{ mb: 0.5 }}
                        >
                            {templates.map(t => (
                                <ToggleButton key={t.name} value={t.name} sx={{ fontSize: 12, px: 2 }}>
                                    {t.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <Typography variant="caption" sx={{ display: "block", color: "#888", mb: 2 }}>
                            {template.description}
                        </Typography>

                        {template.params.map((p, i) => (
                            <TextField
                                key={`${templateName}-${i}`}
                                fullWidth
                                size="small"
                                label={p.label}
                                placeholder={p.example}
                                value={current[i] || ""}
                                onChange={e => setValue(i, e.target.value.replace(/\n/g, " "))}
                                autoFocus={!isNew && i === 0 && !current[0]}
                                sx={{ mb: 1.5 }}
                            />
                        ))}

                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: 10 }}>
                            Así le llega
                        </Typography>
                        <Box sx={{ bgcolor: "#e8f5e9", color: "#1b5e20", borderRadius: 2, px: 1.5, py: 1, mt: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                                {preview}
                            </Typography>
                        </Box>
                    </>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2, fontSize: 12 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={sending} size="small">
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSend}
                    disabled={!completo || sending}
                    startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon fontSize="small" />}
                >
                    Enviar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
